import type { GuardrailStatus } from "./backendTypes";
import type { GprtAttachment } from "../../grouppricingrequests/ActionApis/types";

// Wire-contract enums that pass through the adapter unchanged.
export type { GuardrailStatus };

// ──────────────────────────────────────────────
// Platforms
// ──────────────────────────────────────────────

export interface PlatformDto {
  id: number;
  name: string;
  isActive: boolean;
}

/** Fallback used only when the platforms API is unreachable. */
export const FALLBACK_PLATFORMS: PlatformDto[] = [
  { id: 42, name: "HotelPlanner", isActive: true },
  { id: 18, name: "Cvent", isActive: true },
  { id: 99, name: "HotelEngine", isActive: true },
];

// Mutable id → name map, updated at runtime by usePlatforms; adapters use it for lookups.
export const PLATFORM_MAP: Record<number, string> = Object.fromEntries(
  FALLBACK_PLATFORMS.map((p) => [p.id, p.name])
);

// Merges in rather than replacing — platforms are now fetched per selected
// property, so an earlier property's entries must survive a later property's
// fetch instead of being wiped from the shared lookup map.
export const updatePlatformMap = (platforms: PlatformDto[]) => {
  platforms.forEach((p) => {
    PLATFORM_MAP[p.id] = p.name;
  });
};

// ──────────────────────────────────────────────
// GPC Property Config (from hotel_context)
// ──────────────────────────────────────────────

export interface DemandBand {
  value: number;
  threshold: number;
}

export interface BandConfig {
  low: DemandBand;
  mid: DemandBand;
  high: DemandBand;
}

export type FloorType =
  | "CALCULATED_FROM_ADR"
  | "FETCHED_FROM_SEASONS"
  | "BOTH_HIGHER_OF_TWO"
  | "BOTH_LOWER_OF_TWO";

export type CeilingType =
  | "FETCHED_FROM_SDLY_ADR"
  | "FETCHED_FROM_SEASONS"
  | "BOTH_HIGHER_OF_TWO"
  | "BOTH_LOWER_OF_TWO";

export interface GpcPropertyConfig {
  propertyId: string;
  isGpcActive: boolean;
  totalRooms: number | null;
  reqToTotalRatio: number | null;
  pgOtbRatio: number | null;
  budgetOccRatio: number | null;
  /** Percentages, 0–100. */
  otaRateRangeThreshold: number | null;
  compsetRateRangeThreshold: number | null;
  demandConfig: BandConfig;
  dampeningConfig: BandConfig;
  floorType: FloorType | null;
  ceilingType: CeilingType | null;
}

/**
 * PATCH body. Only the changed keys are sent — an absent key means "leave
 * unchanged", so every field is optional. totalRooms/isGpcActive are read-only
 * server-side and changedBy comes from the JWT, so none of them appear here.
 * The two band configs go up as stringified JSON, matching what GET returns.
 */
export interface UpdatePropertyConfigPayload {
  reqToTotalRatio?: number;
  pgOtbRatio?: number;
  budgetOccRatio?: number;
  otaRateRangeThreshold?: number;
  compsetRateRangeThreshold?: number;
  demandConfig?: string;
  dampeningConfig?: string;
  floorType?: FloorType;
  ceilingType?: CeilingType;
}

// ──────────────────────────────────────────────
// Per-Property/Platform Auto-Approval
// ──────────────────────────────────────────────

/**
 * One backfilled (property, platform) row. Rows are inserted manually via
 * direct SQL — a platform with no row simply doesn't appear in the GET list
 * and is treated as auto-approval disabled (see gpc-auto-approval-api-contract.md).
 */
export interface AutoApprovalConfigDto {
  id: number;
  propertyId: string;
  platformId: number;
  autoApprovalEnabled: boolean;
  updatedAt: string | null;
}

// ──────────────────────────────────────────────
// Pricing Request
// ──────────────────────────────────────────────

export type CommissionType = "PERCENT" | "FLAT" | "NONE";

export type PricingRequestStatus =
  | "PENDING"
  | "PRICED"
  | "HITL_REQUIRED"
  | "FAILED";

export type PricingDecision = "RATE_ACCEPTED" | "RATE_DENIED" | "MANUAL_RATE";

/**
 * Which kind of GPC review is pending on a SARAH-sourced request — non-null
 * only while awaiting a human decision via updateSarahDecision. NOT derivable
 * from `decision` (also null for a SARAH request that priced successfully
 * without ever escalating) or `status` — this is the authoritative signal for
 * whether SarahDecisionCard has anything to act on. SUBMISSION_RATE_REVIEW is
 * a resubmission after the platform rejected the previously submitted rate
 * (see `sarahResumeStatus` ESCALATED) — only MANUAL_RATE/RATE_DENIED are
 * valid decisions for it, same as MANUAL_PRICING_REQUIRED.
 */
export type GpcPendingReviewType =
  | "RATE_REVIEW"
  | "MANUAL_PRICING_REQUIRED"
  | "SUBMISSION_RATE_REVIEW";

/**
 * Outcome of resuming a SARAH-sourced RFP after its GPC decision — i.e. did
 * the proposal actually get generated/submitted back to the platform.
 * Independent of `decision`/`pendingGpcReviewType`, which only capture the
 * GPC-side review itself: null on non-SARAH rows and on SARAH rows whose RFP
 * hasn't reached proposal generation/submission yet. FAILED covers both a
 * business and infra failure (retryable); ESCALATED means submission reached
 * the platform but needs a human to review it there (nothing to retry).
 */
export type SarahResumeStatus =
  | "IN_PROGRESS"
  | "SUCCESS"
  | "FAILED"
  | "ESCALATED";

export type PricingMethod = "GPC" | "12M_PRICING" | "LLM" | "HITL";

export type StrategyOutcome = "SUCCESS" | "FAILED_FALLBACK" | "FAILED_TERMINAL";

export interface ItineraryLine {
  checkinDate: string;
  checkoutDate: string;
  /**
   * A `roomTypeCode`. Not an enum: older rows still carry the normalised
   * buckets (SINGLE, DOUBLE, …) or the literal ANY.
   */
  roomCategory: string;
  roomCount: number;
}

/** Requests created directly on the GPC page, as opposed to GPRT/SARAH. */
export const GPC_UI_SOURCE = "GPC_UI";
/** Requests created via the Group Pricing Request Tool, forwarded into GPC. */
export const GPRT_UI_SOURCE = "GPRT_UI";
/** RFPs SARAH escalated to a human for a rate decision (GPC_RATE_REVIEW /
 * GPC_MANUAL_PRICING_REQUIRED). Approving/declining these goes through the
 * RFP-scoped decision endpoint below, not updateDecision. */
export const SARAH_SOURCE = "SARAH";

// ──────────────────────────────────────────────
// Room Types + Availability
// ──────────────────────────────────────────────

/** One selectable room from the property's own catalogue. */
export interface GpcRoomType {
  roomTypeCode: string;
  // Omitted when null — property_room_details is filled in by hand.
  roomDescription?: string | null;
  totalRooms?: number | null;
}

/** Rooms left for one room type on one night. */
export interface GpcRoomAvailability {
  roomTypeCode: string;
  stayDate: string;
  // Omitted when the ETL has a row but no count for that night.
  availableRooms?: number | null;
}

/** Never null — sarah drops the nights it can't derive rather than sending one. */
export interface GpcPropertyAvailability {
  stayDate: string;
  availableRooms: number;
}

export interface CreatePricingRequestPayload {
  platformRfpId?: string;
  requestSource: "GPC_UI" | "GPRT_UI" | "SARAH";
  platformId: number;
  requestedStartDate: string;
  requestedEndDate: string;
  commissionType?: CommissionType;
  requestedCommissionValue?: number;
  rebateType?: CommissionType;
  requestedRebateValue?: number;
  requestedTotalRooms: number;
  requestedComments?: string;
  itineraries: ItineraryLine[];
}

export interface CreatePricingRequestResponse {
  id: string;
}

export interface RecalculatePricingRequestPayload {
  itineraries: ItineraryLine[];
}

// ──────────────────────────────────────────────
// List Pricing Requests
// ──────────────────────────────────────────────

export interface PricingRequestListItem {
  id: string;
  propertyId: string;
  platformRfpId: string | null;
  requestSource: string;
  requestedBy: string;
  platform: { id: number; name: string };
  requestedStartDate: string;
  requestedEndDate: string;
  nightCount: number;
  requestedTotalRooms: number;
  quotedTotalRooms: number | null;
  quotedTotalPrice: number | null;
  strategyUsed: string | null;
  status: PricingRequestStatus;
  isPartialAllocation: boolean;
  durationMs: number | null;
  createdAt: string;
  decision: PricingDecision | null;
  decisionComment: string | null;
  rateAcknowledgedBy: string | null;
  pendingGpcReviewType: GpcPendingReviewType | null;
}

export interface PricingRequestListResponse {
  content: PricingRequestListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface ListRequestsParams {
  startDate?: string;
  endDate?: string;
  status?: PricingRequestStatus;
  requestSource?: string | string[];
  platformId?: number;
  page?: number;
  size?: number;
  sort?: string;
}

// ──────────────────────────────────────────────
// Pricing Request Detail
// ──────────────────────────────────────────────

export interface PricingRequestDetail {
  id: string;
  propertyId: string;
  platformRfpId: string | null;
  requestSource: string;
  requestedBy: string;
  platform: { id: number; name: string };
  requestedStartDate: string;
  requestedEndDate: string;
  nightCount: number;
  commissionType: CommissionType | null;
  requestedCommissionValue: number | null;
  rebateType: CommissionType | null;
  requestedRebateValue: number | null;
  requestedTotalRooms: number;
  quotedStartDate: string | null;
  quotedEndDate: string | null;
  quotedCommissionValue: number | null;
  quotedRebateValue: number | null;
  quotedTotalRooms: number | null;
  quotedTotalPrice: number | null;
  strategyUsed: string | null;
  status: PricingRequestStatus;
  isPartialAllocation: boolean;
  pricingReasoning: string | null;
  errorReason: string | null;
  errorCode: string | null;
  durationMs: number | null;
  traceId: string | null;
  createdAt: string;
  updatedAt: string;
  decision: PricingDecision | null;
  decisionComment: string | null;
  rateAcknowledgedBy: string | null;
  requestedComments: string | null;
  pendingGpcReviewType: GpcPendingReviewType | null;
  sarahResumeStatus: SarahResumeStatus | null;
  sarahResumeError: string | null;
}

// ──────────────────────────────────────────────
// Pricing Decision Update
// ──────────────────────────────────────────────

export interface UpdateDecisionPayload {
  decision: PricingDecision;
  decisionComment?: string | null;
}

export interface UpdateDecisionResponse {
  id: string;
  decision: PricingDecision;
  decisionComment: string | null;
  rateAcknowledgedBy: string;
}

// ──────────────────────────────────────────────
// SARAH Pricing Decision (RFP-scoped, resumes the SARAH pipeline)
// ──────────────────────────────────────────────

/**
 * PATCH /rfp/{pricingRequestId}/pricing/decision — distinct from
 * UpdateDecisionPayload because approving/declining here also fires the RFP
 * state machine's APPROVE_HITL/DECLINE_HITL event and (on approve) schedules
 * proposal generation, instead of just recording a decision. Field names
 * confirmed against RfpPricingDecisionController/RfpPricingDecisionRequestDto.
 * The endpoint returns 204 No Content for all three decision types — there is
 * no response DTO; callers must refetch to see the outcome (see
 * updateSarahDecision in GpcApis.ts).
 */
export interface UpdateSarahDecisionPayload {
  decision: PricingDecision;
  decisionComment?: string | null;
  manualRateItineraries?: ManualRateItineraryLine[];
}

/**
 * GET /rfp/{pricingRequestId}/pricing/review-status — RFP-owned, kept off
 * PricingRequestDetail for module-boundary reasons (domain/pricing can't read
 * hitl.reviews/rfp.action_invocations). All-null for a non-SARAH row or a
 * SARAH row with no RFP linked yet; 200 always, never errors.
 */
export interface SarahReviewStatus {
  pendingGpcReviewType: GpcPendingReviewType | null;
  sarahResumeStatus: SarahResumeStatus | null;
  sarahResumeError: string | null;
}

// ──────────────────────────────────────────────
// GPRT context on a pricing request (endpoint 6)
// ──────────────────────────────────────────────

export interface GprtContextRequestedItinerary {
  id: string;
  checkinDate: string;
  checkoutDate: string;
  peakRoomCount: number | null;
  roomTypeText: string | null;
}

// pricing.request_itineraries — the GPC engine's CURRENT view of the requested
// itinerary. Diverges from GprtContextRequestedItinerary above (the original,
// write-once request) once an Edit & Recalculate has replaced these rows.
export interface GprtContextCurrentItinerary {
  id: string;
  checkinDate: string;
  checkoutDate: string;
  /** Same widened shape as ItineraryLine.roomCategory — a room code. */
  roomCategory: string;
  roomCount: number;
}

// rfp.quoted_itineraries — the FINAL quote, empty until a decision
// (RATE_ACCEPTED or MANUAL_RATE) is recorded.
export interface GprtContextFinalQuotedItinerary {
  checkinDate: string;
  checkoutDate: string;
  roomTypeText: string | null;
  roomCount: number | null;
  price: number | null;
}

export interface PricingRequestDetailWithGprtContext {
  pricingRequest: PricingRequestDetail;
  requestedItineraries: GprtContextRequestedItinerary[];
  currentRequestedItineraries: GprtContextCurrentItinerary[];
  finalQuotedItineraries: GprtContextFinalQuotedItinerary[];
  // Same shape GPRT's own request detail uses (`GprtAttachment` in
  // grouppricingrequests/ActionApis/types.ts) — no `id`, since the shared
  // AttachmentDto this comes from has none (contract gotcha #15). Shared
  // rather than redeclared so the two can't silently drift apart.
  attachments: GprtAttachment[];
}

// ──────────────────────────────────────────────
// Manual Rate (endpoint 7)
// ──────────────────────────────────────────────

/** Backend's @DecimalMin on ManualRateItineraryDto.price — below this, the submit 400s. */
export const MIN_MANUAL_RATE_PRICE = 35;

export interface ManualRateItineraryLine {
  requestItineraryId?: string | null;
  checkinDate: string;
  checkoutDate: string;
  /** Same widened shape as ItineraryLine.roomCategory — a room code. */
  roomCategory: string;
  // null = not yet entered (incomplete); comped rooms are no longer
  // expressible as 0 here (backend floor is MIN_MANUAL_RATE_PRICE).
  price: number | null;
  roomCount: number;
}

export interface SubmitManualRatePayload {
  itineraries: ManualRateItineraryLine[];
  // Plural — contract's ManualRateRequestDto field name, distinct from
  // UpdateDecisionPayload's singular decisionComment used by the decision endpoints.
  decisionComments?: string | null;
}

// ──────────────────────────────────────────────
// Quoted Itineraries + Market Snapshot
// ──────────────────────────────────────────────

/** Rooms left for one room type on the owning snapshot's stay date. */
export interface RoomTypeAvailabilityEntry {
  roomTypeCode: string;
  availableRooms: number | null;
}

export interface MarketSnapshot {
  /** When this row was written — the OTB card's "As at" date. */
  capturedAt: string | null;
  /**
   * `null` = nothing was captured for this date (the request predates the
   * snapshot, or core-service was down); `[]` = captured, but the occupancy ETL
   * covers no room type at this property.
   */
  roomTypeAvailability: RoomTypeAvailabilityEntry[] | null;
  occupiedRooms: number | null;
  inventory: number | null;
  ooo: number;
  occupancyPct: number;
  sdlyOccupancy: number;
  adr: number;
  sdlyAdr: number;
  sdllyAdr: number;
  stlyAdr: number;
  stlyOtb: number;
  sdlyOtb: number;
  pace: number;
  hotelBarRate: number;
  hotelLowestRate: number;
  avgCompsetBarRate: number;
  avgCompsetExpediaRate: number;
  totalBudget: number;
  expediaRoomCode: string | null;
  blackoutReason: string | null;
}

export interface QuotedItineraryItem {
  stayDate: string;
  roomType: string | null;
  requestedRooms: number | null;
  allocatedRooms: number | null;
  sellRate: number | null;
  pricingMethod: PricingMethod | null;
  allocationReasoning: string | null;
  marketSnapshot: MarketSnapshot;
}

export interface QuotedItinerariesResponse {
  requestId: string;
  requestedStartDate: string;
  requestedEndDate: string;
  items: QuotedItineraryItem[];
}

// ──────────────────────────────────────────────
// Request Summary
// ──────────────────────────────────────────────

export interface DateRangeSummary {
  totalNights: number;
  overallWeightedAvgSellRate: number;
  minSellRateAnyNight: number;
  maxSellRateAnyNight: number;
  totalQuotedPrice: number;
}

export interface RoomTypeSummaryItem {
  roomType: string;
  avgSellRateOverRange: number;
  minSellRate: number;
  maxSellRate: number;
  nightsWithAllocation: number;
}

export interface RequestSummaryResponse {
  requestId: string;
  dateRangeSummary: DateRangeSummary;
  roomTypeSummary: RoomTypeSummaryItem[];
}

// ──────────────────────────────────────────────
// Pricing Method Audits
// ──────────────────────────────────────────────

export interface GuardrailResult {
  label: string;
  status: GuardrailStatus;
  value: number | null;
  threshold: number | null;
  detail: string | null;
}

export interface FormulaBreakdown {
  ceiling: number;
  floorRbrc: number;
  floorMinAdr: number;
  floor: number;
  width: number;
  budgetOccPercent: number;
  demandFactor: number;
  dampenerRatio: number;
  dampenerFactor: number;
  rawPrice: number;
  finalPrice: number;
  differential: number;
  priceWithDifferential: number;
  sellPrice: number;
  cappedPrice: number;
  commissionType: string | null;
  rebateType: string | null;
  commissionValue: number | null;
  rebateValue: number | null;
  degenerateCorridor: boolean;
  widthNote: string | null;
}

export interface AuditItem {
  id: string;
  stayDate: string;
  roomType: string;
  pricingMethod: PricingMethod;
  strategyOutcome: StrategyOutcome;
  attemptOrder: number;
  allocatedRooms: number | null;
  sellRate: number | null;
  finalSellRate: number | null;
  compsetCapApplied: boolean;
  compsetCapRate: number | null;
  errorReason: string | null;
  blackoutReason: string | null;
  durationMs: number;
  guardrailResults: Record<string, GuardrailResult> | null;
  formulaBreakdown: FormulaBreakdown | null;
}

export interface AuditsResponse {
  requestId: string;
  hasWarnings: boolean;
  totalAttempts: number;
  failedAttempts: number;
  items: AuditItem[];
}

export interface AuditQueryParams {
  stayDate?: string;
  hasWarnings?: boolean;
}
